
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { playerRegistrationService } from '@/services/playerRegistrationService';
import { PlayerRegistrationStatus, RegistrationStatus, TeamAssignmentStatus, PlayerAvailabilityStatus } from '@/types/database';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

interface PlayerRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamId?: number;
  teamName?: string;
}

interface Team {
  team_id: number;
  team_name: string;
}

interface PlayerInfo {
  id: string;
  username?: string;
  first_name?: string;
  last_name?: string;
}

const PlayerRegistrationModal = ({ isOpen, onClose, teamId, teamName }: PlayerRegistrationModalProps) => {
  const { user } = useAuth();
  const [registrations, setRegistrations] = useState<PlayerRegistrationStatus[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState<PlayerRegistrationStatus | null>(null);
  const [coachTeams, setCoachTeams] = useState<Team[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(teamId || null);
  const [selectedTeamName, setSelectedTeamName] = useState<string>(teamName || '');
  const [playerInfo, setPlayerInfo] = useState<Record<string, PlayerInfo>>({});
  const [updateData, setUpdateData] = useState({
    registration_status: '' as RegistrationStatus,
    team_assignment_status: '' as TeamAssignmentStatus,
    availability_status: '' as PlayerAvailabilityStatus,
    assigned_team_name: '',
    status_notes: ''
  });

  // Load coach teams
  const loadCoachTeams = async () => {
    if (!user) return;
    
    try {
      const { data: teams, error } = await supabase
        .from('teams')
        .select('team_id, team_name')
        .or(`created_by.eq.${user.id},team_id.in.(${
          await supabase
            .from('team_coaches')
            .select('team_id')
            .eq('coach_id', user.id)
            .then(({ data }) => data?.map(tc => tc.team_id).join(',') || '0')
        })`);

      if (error) throw error;
      setCoachTeams(teams || []);
      
      // If no specific team was provided, select the first team
      if (!selectedTeamId && teams && teams.length > 0) {
        setSelectedTeamId(teams[0].team_id);
        setSelectedTeamName(teams[0].team_name);
      }
    } catch (error) {
      console.error('Error loading coach teams:', error);
    }
  };

  const loadPlayerInfo = async (userIds: string[]) => {
    if (userIds.length === 0) return;
    
    try {
      const { data: profiles, error } = await supabase
        .from('profiles')
        .select('id, username, first_name, last_name')
        .in('id', userIds);

      if (error) throw error;
      
      const playerInfoMap: Record<string, PlayerInfo> = {};
      profiles?.forEach(profile => {
        playerInfoMap[profile.id] = profile;
      });
      setPlayerInfo(playerInfoMap);
    } catch (error) {
      console.error('Error loading player info:', error);
    }
  };

  const loadData = async () => {
    if (!selectedTeamId) return;
    
    try {
      setLoading(true);
      const [registrationsData, statsData] = await Promise.all([
        playerRegistrationService.getTeamRegistrations(selectedTeamId),
        playerRegistrationService.getTeamRegistrationStats(selectedTeamId)
      ]);
      setRegistrations(registrationsData);
      setStats(statsData);
      
      // Load player information for all registrations
      const userIds = registrationsData.map(reg => reg.user_id);
      await loadPlayerInfo(userIds);
    } catch (error) {
      console.error('Error loading registration data:', error);
      toast({
        title: "Error",
        description: "Failed to load registration data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRegistration = async () => {
    if (!selectedRegistration || !user) return;

    try {
      setLoading(true);
      const updates: any = {
        status_updated_by: user.id
      };

      if (updateData.registration_status) updates.registration_status = updateData.registration_status;
      if (updateData.team_assignment_status) updates.team_assignment_status = updateData.team_assignment_status;
      if (updateData.availability_status) updates.availability_status = updateData.availability_status;
      if (updateData.assigned_team_name) updates.assigned_team_name = updateData.assigned_team_name;
      if (updateData.status_notes) updates.status_notes = updateData.status_notes;

      await playerRegistrationService.updateRegistrationStatus(selectedRegistration.id, updates);
      toast({
        title: "Success",
        description: "Registration status updated successfully"
      });
      setSelectedRegistration(null);
      setUpdateData({
        registration_status: '' as RegistrationStatus,
        team_assignment_status: '' as TeamAssignmentStatus,
        availability_status: '' as PlayerAvailabilityStatus,
        assigned_team_name: '',
        status_notes: ''
      });
      await loadData();
    } catch (error) {
      console.error('Error updating registration:', error);
      toast({
        title: "Error",
        description: "Failed to update registration status",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string, type: string) => {
    const colors = {
      registration: {
        open: 'bg-green-100 text-green-800',
        closed: 'bg-red-100 text-red-800',
        waitlisted: 'bg-yellow-100 text-yellow-800',
        pending_approval: 'bg-blue-100 text-blue-800',
        approved: 'bg-green-100 text-green-800',
        declined: 'bg-red-100 text-red-800'
      },
      assignment: {
        unassigned: 'bg-gray-100 text-gray-800',
        assigned: 'bg-green-100 text-green-800',
        roster_open: 'bg-blue-100 text-blue-800',
        roster_locked: 'bg-red-100 text-red-800'
      },
      availability: {
        active: 'bg-green-100 text-green-800',
        inactive: 'bg-gray-100 text-gray-800',
        injured: 'bg-red-100 text-red-800',
        doubtful: 'bg-orange-100 text-orange-800',
        questionable: 'bg-yellow-100 text-yellow-800',
        probable: 'bg-blue-100 text-blue-800',
        out: 'bg-red-100 text-red-800',
        suspended: 'bg-purple-100 text-purple-800',
        pup: 'bg-red-100 text-red-800',
        eligible_verified: 'bg-green-100 text-green-800',
        play_up_request: 'bg-blue-100 text-blue-800'
      }
    };
    return colors[type as keyof typeof colors]?.[status as keyof any] || 'bg-gray-100 text-gray-800';
  };

  const getPlayerDisplayName = (userId: string) => {
    const player = playerInfo[userId];
    if (!player) return `Player ${userId.substring(0, 8)}`;
    
    if (player.first_name && player.last_name) {
      return `${player.first_name} ${player.last_name}`;
    }
    if (player.username) {
      return player.username;
    }
    return `Player ${userId.substring(0, 8)}`;
  };

  const handleTeamChange = (teamId: string) => {
    const team = coachTeams.find(t => t.team_id === parseInt(teamId));
    if (team) {
      setSelectedTeamId(team.team_id);
      setSelectedTeamName(team.team_name);
      setSelectedRegistration(null); // Clear selected registration when switching teams
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadCoachTeams();
    }
  }, [isOpen, user]);

  useEffect(() => {
    if (selectedTeamId) {
      loadData();
    }
  }, [selectedTeamId]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">
            Player Registrations
            {coachTeams.length > 1 ? '' : selectedTeamName ? ` - ${selectedTeamName}` : ''}
          </DialogTitle>
        </DialogHeader>

        {/* Team Selector - Only show if coach has multiple teams */}
        {coachTeams.length > 1 && (
          <div className="mb-4">
            <Label htmlFor="team-select" className="text-white mb-2 block">Select Team</Label>
            <Select value={selectedTeamId?.toString() || ''} onValueChange={handleTeamChange}>
              <SelectTrigger className="bg-gray-600 border-gray-500 text-white">
                <SelectValue placeholder="Select a team" />
              </SelectTrigger>
              <SelectContent className="bg-gray-600 border-gray-500">
                {coachTeams.map((team) => (
                  <SelectItem key={team.team_id} value={team.team_id.toString()}>
                    {team.team_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {!selectedTeamId ? (
          <div className="text-center py-8 text-gray-400">
            Please select a team to view registrations.
          </div>
        ) : (

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="registrations">Registrations</TabsTrigger>
            <TabsTrigger value="update">Update Status</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-gray-700 border-gray-600">
                  <CardHeader>
                    <CardTitle className="text-sports-green">Registration Status</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {Object.entries(stats.registrationStatus).map(([status, count]) => (
                      <div key={status} className="flex justify-between">
                        <span className="capitalize">{status.replace('_', ' ')}</span>
                        <Badge variant="outline" className="text-white">{Number(count)}</Badge>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card className="bg-gray-700 border-gray-600">
                  <CardHeader>
                    <CardTitle className="text-sports-green">Team Assignment</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {Object.entries(stats.teamAssignment).map(([status, count]) => (
                      <div key={status} className="flex justify-between">
                        <span className="capitalize">{status.replace('_', ' ')}</span>
                        <Badge variant="outline" className="text-white">{Number(count)}</Badge>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card className="bg-gray-700 border-gray-600">
                  <CardHeader>
                    <CardTitle className="text-sports-green">Availability</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {Object.entries(stats.availability).filter(([_, count]) => Number(count) > 0).map(([status, count]) => (
                      <div key={status} className="flex justify-between">
                        <span className="capitalize">{status.replace('_', ' ')}</span>
                        <Badge variant="outline" className="text-white">{Number(count)}</Badge>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          <TabsContent value="registrations" className="space-y-4">
            <div className="space-y-3">
              {registrations.map((registration) => (
                <Card key={registration.id} className="bg-gray-700 border-gray-600">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-white">{getPlayerDisplayName(registration.user_id)}</p>
                        <div className="flex gap-2 mt-2">
                          <Badge className={getStatusColor(registration.registration_status, 'registration')}>
                            {registration.registration_status}
                          </Badge>
                          {registration.team_assignment_status && (
                            <Badge className={getStatusColor(registration.team_assignment_status, 'assignment')}>
                              {registration.team_assignment_status}
                            </Badge>
                          )}
                          {registration.availability_status && (
                            <Badge className={getStatusColor(registration.availability_status, 'availability')}>
                              {registration.availability_status}
                            </Badge>
                          )}
                        </div>
                        {registration.status_notes && (
                          <p className="text-sm text-gray-300 mt-1">{registration.status_notes}</p>
                        )}
                      </div>
                      <Button
                        onClick={() => setSelectedRegistration(registration)}
                        size="sm"
                        className="bg-sports-green text-black hover:bg-green-600"
                      >
                        Update
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="update" className="space-y-4">
            {selectedRegistration ? (
              <Card className="bg-gray-700 border-gray-600">
                <CardHeader>
                  <CardTitle className="text-white">Update Registration Status</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="reg-status" className="text-white">Registration Status</Label>
                      <Select value={updateData.registration_status} onValueChange={(value) => setUpdateData({...updateData, registration_status: value as RegistrationStatus})}>
                        <SelectTrigger className="bg-gray-600 border-gray-500 text-white">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-600 border-gray-500">
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                          <SelectItem value="waitlisted">Waitlisted</SelectItem>
                          <SelectItem value="pending_approval">Pending Approval</SelectItem>
                          <SelectItem value="approved">Approved</SelectItem>
                          <SelectItem value="declined">Declined</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="assignment-status" className="text-white">Team Assignment</Label>
                      <Select value={updateData.team_assignment_status} onValueChange={(value) => setUpdateData({...updateData, team_assignment_status: value as TeamAssignmentStatus})}>
                        <SelectTrigger className="bg-gray-600 border-gray-500 text-white">
                          <SelectValue placeholder="Select assignment" />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-600 border-gray-500">
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          <SelectItem value="assigned">Assigned</SelectItem>
                          <SelectItem value="roster_open">Roster Open</SelectItem>
                          <SelectItem value="roster_locked">Roster Locked</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="availability-status" className="text-white">Availability Status</Label>
                      <Select value={updateData.availability_status} onValueChange={(value) => setUpdateData({...updateData, availability_status: value as PlayerAvailabilityStatus})}>
                        <SelectTrigger className="bg-gray-600 border-gray-500 text-white">
                          <SelectValue placeholder="Select availability" />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-600 border-gray-500">
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                          <SelectItem value="injured">Injured</SelectItem>
                          <SelectItem value="doubtful">Doubtful</SelectItem>
                          <SelectItem value="questionable">Questionable</SelectItem>
                          <SelectItem value="probable">Probable</SelectItem>
                          <SelectItem value="out">Out</SelectItem>
                          <SelectItem value="suspended">Suspended</SelectItem>
                          <SelectItem value="pup">PUP</SelectItem>
                          <SelectItem value="eligible_verified">Eligible/Verified</SelectItem>
                          <SelectItem value="play_up_request">Play-Up Request</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="team-name" className="text-white">Assigned Team Name</Label>
                      <input
                        id="team-name"
                        type="text"
                        value={updateData.assigned_team_name}
                        onChange={(e) => setUpdateData({...updateData, assigned_team_name: e.target.value})}
                        className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white"
                        placeholder="Enter team name"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="notes" className="text-white">Status Notes</Label>
                    <Textarea
                      id="notes"
                      value={updateData.status_notes}
                      onChange={(e) => setUpdateData({...updateData, status_notes: e.target.value})}
                      className="bg-gray-600 border-gray-500 text-white"
                      placeholder="Add notes about the status change..."
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={handleUpdateRegistration}
                      disabled={loading}
                      className="bg-sports-green text-black hover:bg-green-600"
                    >
                      Update Status
                    </Button>
                    <Button
                      onClick={() => setSelectedRegistration(null)}
                      variant="outline"
                      className="border-gray-500 text-white hover:bg-gray-600"
                    >
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <p className="text-gray-400">Select a registration from the Registrations tab to update its status.</p>
            )}
          </TabsContent>
        </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PlayerRegistrationModal;
